import React, { useState, useEffect } from 'react';
import { ref, onValue, update } from 'firebase/database';
import { signOut } from 'firebase/auth';
import { database, auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import PlanManager from '../components/PlanManager';
import InitializePlans from '../components/InitializePlans';
import AdminChat from '../components/AdminChat';
import Charts from '../components/Charts';
import ContentManagement from './ContentManagement';
import { getGroqApiKey, saveGroqApiKey, clearGroqApiKey } from '../services/apiKeysService';

export default function Dashboard() {
    const [users, setUsers] = useState({});
    const [price, setPrice] = useState('');
    const [loading, setLoading] = useState(true);
    const [editingLimit, setEditingLimit] = useState(null);
    const [tempLimit, setTempLimit] = useState('');
    const [editingGroqKey, setEditingGroqKey] = useState(null);
    const [tempGroqKey, setTempGroqKey] = useState('');
    const [groqApiKeys, setGroqApiKeys] = useState({}); // { nutritionistId: apiKey }
    const [patientCounts, setPatientCounts] = useState({}); // { nutritionistId: count }
    const [activeTab, setActiveTab] = useState('users'); // 'users' or 'plans'
    const navigate = useNavigate();

    useEffect(() => {
        const usersRef = ref(database, 'users');
        const priceRef = ref(database, 'settings/priceAfterTest');
        const apiKeysRef = ref(database, 'apiKeys');

        const unsubscribeUsers = onValue(usersRef, (snapshot) => {
            const data = snapshot.val();
            setUsers(data || {});
            setLoading(false);
        });

        const unsubscribePrice = onValue(priceRef, (snapshot) => {
            setPrice(snapshot.val() || '');
        });

        const unsubscribeApiKeys = onValue(apiKeysRef, async (snapshot) => {
            const apiKeysData = snapshot.val() || {};
            const keysMap = {};
            
            // Carregar chaves do Groq para cada nutricionista
            for (const [nutritionistId, keys] of Object.entries(apiKeysData)) {
                if (keys && keys.groq) {
                    keysMap[nutritionistId] = keys.groq;
                }
            }
            
            setGroqApiKeys(keysMap);
        });

        // Buscar contagem de pacientes por nutricionista
        const relationshipsRef = ref(database, 'relationships');
        const unsubscribeRelationships = onValue(relationshipsRef, (snapshot) => {
            const relationships = snapshot.val() || {};
            const counts = {};
            
            // Contar pacientes por nutricionista
            Object.values(relationships).forEach(rel => {
                if (rel && rel.nutritionistId) {
                    const nutritionistId = rel.nutritionistId;
                    counts[nutritionistId] = (counts[nutritionistId] || 0) + 1;
                }
            });
            
            setPatientCounts(counts);
        });

        return () => {
            unsubscribeUsers();
            unsubscribePrice();
            unsubscribeApiKeys();
            unsubscribeRelationships();
        };
    }, []);

    const handleLogout = async () => {
        await signOut(auth);
        navigate('/login');
    };

    const toggleBlock = async (userId, currentStatus) => {
        const userRef = ref(database, `users/${userId}`);
        await update(userRef, {
            blocked: !currentStatus
        });
    };

    const savePrice = async () => {
        const priceRef = ref(database, 'settings');
        await update(priceRef, {
            priceAfterTest: price
        });
        alert('Valor salvo com sucesso!');
    };

    const startEditingLimit = (userId, currentLimit) => {
        setEditingLimit(userId);
        setTempLimit(currentLimit || '5'); // Default 5 se não existir
    };

    const saveLimit = async (userId) => {
        const userRef = ref(database, `users/${userId}`);
        await update(userRef, {
            maxPatients: parseInt(tempLimit)
        });
        setEditingLimit(null);
    };

    const startEditingGroqKey = (userId, currentKey) => {
        setEditingGroqKey(userId);
        setTempGroqKey(currentKey || '');
    };

    const saveGroqKey = async (userId) => {
        try {
            if (tempGroqKey.trim()) {
                await saveGroqApiKey(userId, tempGroqKey.trim());
                alert('Chave do Groq salva com sucesso!');
            } else {
                // Se vazio, remover a chave
                await clearGroqApiKey(userId);
                alert('Chave do Groq removida!');
            }
            setEditingGroqKey(null);
            setTempGroqKey('');
        } catch (error) {
            alert(`Erro ao salvar chave: ${error.message}`);
        }
    };

    const clearGroqKey = async (userId) => {
        if (!window.confirm('Tem certeza que deseja remover a chave do Groq deste nutricionista?')) {
            return;
        }
        try {
            await clearGroqApiKey(userId);
            alert('Chave do Groq removida!');
        } catch (error) {
            alert(`Erro ao remover chave: ${error.message}`);
        }
    };

    // Filtrar apenas nutricionistas
    const userList = Object.entries(users)
        .map(([id, data]) => ({
            id,
            ...data
        }))
        .filter(user => user.role === 'nutritionist');

    const totalUsers = userList.length;
    const blockedUsers = userList.filter(u => u.blocked).length;
    const activeUsers = totalUsers - blockedUsers;
    const totalPatients = Object.values(patientCounts).reduce((sum, count) => sum + count, 0);

    if (loading) return <div className="container" style={{ textAlign: 'center', marginTop: '5rem' }}>Carregando...</div>;

    return (
        <div className="container">
            <header className="dashboard-header">
                <div>
                    <h1 style={{ fontSize: '2rem', fontWeight: 700 }}>Painel Administrativo</h1>
                    <p style={{ color: 'var(--text-muted)' }}>Nutrisync - Gerenciamento de Nutricionistas</p>
                </div>
                <button onClick={handleLogout} className="btn btn-danger">
                    Sair
                </button>
            </header>

            {/* Navigation Tabs */}
            <div className="dashboard-tabs">
                <button
                    className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
                    onClick={() => setActiveTab('users')}
                >
                    <span className="tab-icon">👥</span>
                    Nutricionistas
                </button>
                <button
                    className={`tab-button ${activeTab === 'plans' ? 'active' : ''}`}
                    onClick={() => setActiveTab('plans')}
                >
                    <span className="tab-icon">💎</span>
                    Planos de Assinatura
                </button>
                <button
                    className={`tab-button ${activeTab === 'setup' ? 'active' : ''}`}
                    onClick={() => setActiveTab('setup')}
                >
                    <span className="tab-icon">⚙️</span>
                    Setup Inicial
                </button>
                <button
                    className={`tab-button ${activeTab === 'support' ? 'active' : ''}`}
                    onClick={() => setActiveTab('support')}
                >
                    <span className="tab-icon">💬</span>
                    Suporte
                </button>
                <button
                    className={`tab-button ${activeTab === 'charts' ? 'active' : ''}`}
                    onClick={() => setActiveTab('charts')}
                >
                    <span className="tab-icon">📊</span>
                    Gráficos
                </button>
                <button
                    className={`tab-button ${activeTab === 'content' ? 'active' : ''}`}
                    onClick={() => setActiveTab('content')}
                >
                    <span className="tab-icon">📦</span>
                    Conteúdo
                </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'users' ? (
                <>
                    <section className="stats-grid">
                        <div className="stat-card">
                            <div className="stat-label">Total de Nutricionistas</div>
                            <div className="stat-value">{totalUsers}</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-label">Ativos</div>
                            <div className="stat-value" style={{ color: '#00D09C' }}>{activeUsers}</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-label">Bloqueados</div>
                            <div className="stat-value" style={{ color: 'var(--danger)' }}>{blockedUsers}</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-label">Total de Clientes</div>
                            <div className="stat-value" style={{ color: '#8b5cf6' }}>{totalPatients}</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-label">Valor Pós-Teste</div>
                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                                <input
                                    type="text"
                                    value={price}
                                    onChange={(e) => setPrice(e.target.value)}
                                    placeholder="R$ 0,00"
                                    style={{ marginBottom: 0, width: '100px' }}
                                />
                                <button onClick={savePrice} className="btn btn-primary" style={{ padding: '0.5rem 1rem' }}>
                                    Salvar
                                </button>
                            </div>
                        </div>
                    </section>

                    <section className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>Nome</th>
                                    <th>Email</th>
                                    <th>Clientes</th>
                                    <th>Limite Pacientes</th>
                                    <th>API Groq</th>
                                    <th>Status</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {userList.map(user => (
                                    <tr key={user.id}>
                                        <td>{user.name || 'Sem nome'}</td>
                                        <td>{user.email || 'Sem email'}</td>
                                        <td>
                                            <span style={{ 
                                                fontSize: '1rem', 
                                                fontWeight: 600,
                                                color: '#3b82f6'
                                            }}>
                                                {patientCounts[user.id] || 0}
                                            </span>
                                        </td>
                                        <td>
                                            {editingLimit === user.id ? (
                                                <div style={{ display: 'flex', gap: '5px' }}>
                                                    <input
                                                        type="number"
                                                        value={tempLimit}
                                                        onChange={(e) => setTempLimit(e.target.value)}
                                                        style={{ width: '60px', marginBottom: 0, padding: '5px' }}
                                                    />
                                                    <button
                                                        onClick={() => saveLimit(user.id)}
                                                        className="btn btn-primary"
                                                        style={{ padding: '5px 10px', fontSize: '0.8rem' }}
                                                    >
                                                        OK
                                                    </button>
                                                </div>
                                            ) : (
                                                <div
                                                    onClick={() => startEditingLimit(user.id, user.maxPatients)}
                                                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}
                                                    title="Clique para editar"
                                                >
                                                    {user.maxPatients || 'Ilimitado'}
                                                    <span style={{ fontSize: '0.8rem', opacity: 0.5 }}>✎</span>
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {editingGroqKey === user.id ? (
                                                <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                                    <input
                                                        type="password"
                                                        value={tempGroqKey}
                                                        onChange={(e) => setTempGroqKey(e.target.value)}
                                                        placeholder="gsk_..."
                                                        style={{ 
                                                            width: '200px', 
                                                            marginBottom: 0, 
                                                            padding: '5px',
                                                            fontSize: '0.85rem'
                                                        }}
                                                    />
                                                    <button
                                                        onClick={() => saveGroqKey(user.id)}
                                                        className="btn btn-primary"
                                                        style={{ padding: '5px 10px', fontSize: '0.8rem' }}
                                                    >
                                                        OK
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setEditingGroqKey(null);
                                                            setTempGroqKey('');
                                                        }}
                                                        className="btn"
                                                        style={{ 
                                                            padding: '5px 10px', 
                                                            fontSize: '0.8rem',
                                                            background: '#6b7280',
                                                            color: 'white'
                                                        }}
                                                    >
                                                        Cancelar
                                                    </button>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    {groqApiKeys[user.id] ? (
                                                        <>
                                                            <span 
                                                                style={{ 
                                                                    fontSize: '0.85rem',
                                                                    color: '#10b981',
                                                                    fontWeight: 500
                                                                }}
                                                                title="Chave configurada"
                                                            >
                                                                ✓ Configurada
                                                            </span>
                                                            <button
                                                                onClick={() => startEditingGroqKey(user.id, groqApiKeys[user.id])}
                                                                className="btn"
                                                                style={{ 
                                                                    padding: '3px 8px', 
                                                                    fontSize: '0.75rem',
                                                                    background: '#3b82f6',
                                                                    color: 'white'
                                                                }}
                                                                title="Editar chave"
                                                            >
                                                                Editar
                                                            </button>
                                                            <button
                                                                onClick={() => clearGroqKey(user.id)}
                                                                className="btn"
                                                                style={{ 
                                                                    padding: '3px 8px', 
                                                                    fontSize: '0.75rem',
                                                                    background: '#ef4444',
                                                                    color: 'white'
                                                                }}
                                                                title="Remover chave"
                                                            >
                                                                Remover
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span 
                                                                style={{ 
                                                                    fontSize: '0.85rem',
                                                                    color: '#6b7280'
                                                                }}
                                                            >
                                                                Não configurada
                                                            </span>
                                                            <button
                                                                onClick={() => startEditingGroqKey(user.id, '')}
                                                                className="btn btn-primary"
                                                                style={{ 
                                                                    padding: '3px 8px', 
                                                                    fontSize: '0.75rem'
                                                                }}
                                                                title="Adicionar chave"
                                                            >
                                                                Adicionar
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`status-badge ${user.blocked ? 'status-blocked' : 'status-active'}`}>
                                                {user.blocked ? 'Bloqueado' : 'Ativo'}
                                            </span>
                                        </td>
                                        <td>
                                            <button
                                                onClick={() => toggleBlock(user.id, user.blocked)}
                                                className={`btn ${user.blocked ? 'btn-primary' : 'btn-danger'}`}
                                                style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem' }}
                                            >
                                                {user.blocked ? 'Desbloquear' : 'Bloquear'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                </>
            ) : activeTab === 'plans' ? (
                <PlanManager />
            ) : activeTab === 'support' ? (
                <AdminChat />
            ) : activeTab === 'charts' ? (
                <Charts users={users} />
            ) : activeTab === 'content' ? (
                <ContentManagement />
            ) : (
                <InitializePlans />
            )}
        </div>
    );
}
